/**
 * 处理添加购物车操作
 */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false;
        this.error = false;
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this)); // 添加提交购物车监听

        this.addCartButtons = this.querySelectorAll('button[type="submit"]'); // 添加购物车按钮，可能有多处按钮

        this.cartDrawer = document.getElementById("Cart-Drawer");
        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        )
          this.addCartButtons.forEach((button) => {
            button.setAttribute("aria-haspopup", "dialog");
            button.setAttribute("aria-expanded", "false");
            button.setAttribute("aria-controls", "Cart-Drawer");
          });

        this.errorMessageWrapper = document.getElementById(
          `Product-Form-Error-Message-${this.dataset.section}`,
        );
        // 快速购物弹窗
        this.quickAddDrawer = this.closest("product-side-drawer");

        // 迷你结账
        this.miniChecker = document.getElementById(
          `Product-Mini-Checkout-${this.dataset.section}`,
        );
        if (this.miniChecker) {
          // PE 2026-10-04 PDP restyle C30: the bar shows only while the buy block is out of the
          // area below the sticky header (either direction, incl. first load), never over it.
          const buyBlock = this.closest(".product-buy-button") || this;
          const headerH = Math.floor(
            document.querySelector("header")?.getBoundingClientRect().height || 0,
          ); // read once at init (R11)
          const root = document.documentElement;
          // Verify fix 2026-10-05 (G9): the header height is subtracted only while the on-scroll-up header is
          // shown (sticky-header.js stamps html[data-header-status]); while it is hidden the whole viewport counts.
          const observeBuyBlock = () => {
            const top =
              root.getAttribute("data-header-status") === "hidden" ? 0 : headerH;
            if (top === this.miniCheckerTop) return;
            this.miniCheckerTop = top;
            if (this.miniCheckerObserver) this.miniCheckerObserver.disconnect();
            this.miniCheckerObserver = new IntersectionObserver(
              (entries) => {
                const entry = entries[entries.length - 1];
                this.buyBlockOutOfView = !entry.isIntersecting;
                if (entry.isIntersecting) {
                  this.hideMiniCheck();
                } else if (!this.variantPanelOpen) {
                  this.showMiniCheck();
                }
              },
              {
                root: null,
                rootMargin: `-${top}px 0px 0px 0px`,
                threshold: 0,
              },
            );
            this.miniCheckerObserver.observe(buyBlock);
          };
          observeBuyBlock();
          this.headerStatusObserver = new MutationObserver(observeBuyBlock);
          this.headerStatusObserver.observe(root, {
            attributes: true,
            attributeFilter: ["data-header-status"],
          });
        }

        // 迷你结账 - 多变体选择面板
        // 多变体产品在迷你结账点击“+”时，如果用户尚未主动选择变体，
        // 弹出变体选择面板（复用产品页真实的变体选择器）。
        this.variantPanel = document.getElementById(
          `Product-Mini-Variant-${this.dataset.section}`,
        );
        if (this.variantPanel && this.miniChecker) {
          this.miniAddButton = this.miniChecker.querySelector(".mini-check-add");
          this.variantSlot =
            this.variantPanel.querySelector("[data-variant-slot]");
          this.variantCloseButton = this.variantPanel.querySelector(
            ".product-mini-variant__close",
          );
          this.mainSubmitButton = document.getElementById(
            `Product-Submit-Button-${this.dataset.section}`,
          );
          // 产品选项组数量（如 仅尺码 = 1，颜色+尺码 = 2）
          this.optionCount =
            parseInt(this.variantPanel.dataset.optionCount, 10) || 1;
          // 用户在面板内已主动选择过的选项组（按选项名去重）
          this.chosenGroups = new Set();
          this.userChoseVariant = false;
          this.variantPanelOpen = false;

          if (this.miniAddButton)
            this.miniAddButton.addEventListener(
              "click",
              this.onMiniAddClick.bind(this),
            );
          if (this.variantCloseButton)
            this.variantCloseButton.addEventListener("click", () =>
              this.closeVariantPanel(),
            );

          // 面板内选择器交互：记录已选选项组
          if (this.variantSlot) {
            this.variantSlot.addEventListener(
              "change",
              this.onPickerChange.bind(this),
            );
            this.variantSlot.addEventListener(
              "click",
              this.onPickerClick.bind(this),
            );
          }

          // 记录用户是否主动选择过变体（来自产品页的变体选择器）。
          // variantChange 仅在解析出有效可购买变体时触发；面板打开时借此尝试自动加入。
          this.variantChoiceUnsubscriber = webvista.subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              if (event.data.sectionId !== this.dataset.section) return;
              this.userChoseVariant = true;
              if (this.variantPanelOpen) this.maybeAutoAdd();
            },
          );
        }
      }

      disconnectedCallback() {
        if (this.miniCheckerObserver) this.miniCheckerObserver.disconnect();
        if (this.headerStatusObserver) this.headerStatusObserver.disconnect();
        if (this.variantChoiceUnsubscriber) this.variantChoiceUnsubscriber();
        if (this.onDocPointer)
          document.removeEventListener("pointerdown", this.onDocPointer);
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      /**
       * 是否隐藏错误信息
       * 当产品是礼品卡的时候，隐藏错误信息
       * @returns {boolean}
       */
      get hideErrors() {
        return this.dataset.hideErrors === "true";
      }

      /**
       * 处理点击提交购物车
       * @param event
       */
      onSubmitHandler(event) {
        if (this.isloading) return;
        event.preventDefault();

        if (
          Array.from(this.addCartButtons).find((button) =>
            button.hasAttribute("disabled"),
          )
        )
          return;

        this.handleErrorMessage();
        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers[
          "Content-Type"
        ]; /* 浏览器会自动为 formData 对象生成带有边界(boundary)的 multipart/form-data 类型*/

        const formData = new FormData(this.form);
        formData.append(
          "sections",
          this.getSectionsToRender()
            .map((section) => section.section)
            .join(),
        );
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              return (this.error = true);
            }
            this.error = false;

            // 动态更新Sections内容
            SectionDynamicUpdate.updateSections(
              this.getSectionsToRender(),
              response["sections"],
            );

            webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
              cartData: response,
            });

            if (
              this.cartDrawer &&
              !this.cartDrawer.hasAttribute("data-status-silence")
            ) {
              // 打开抽屉
              this.cartDrawer.show(event.submitter);
            } else {
              return (window.location = window["routes"]["cart_url"]);
            }
          })
          .catch((e) => {
            this.handleErrorMessage(
              window["accessibilityStrings"]["unknownError"],
            );
            this.error = true;
          })
          .finally(() => {
            this.endLoading();
            // 关闭迷你结账
            this.hideMiniCheck();
            // 关闭变体选择面板（如果打开）
            if (this.variantPanelOpen) this.closeVariantPanel();
          });
      }

      /**
       * 迷你结账“+”点击处理（仅多变体产品）
       * 用户已主动选择且当前变体可购买 -> 直接加入购物车；
       * 否则弹出变体选择面板。
       */
      onMiniAddClick(event) {
        event.preventDefault();
        if (this.isloading) return;

        const available =
          this.mainSubmitButton &&
          !this.mainSubmitButton.hasAttribute("disabled");

        if (this.userChoseVariant && available) {
          this.addToCart();
        } else {
          this.openVariantPanel();
        }
      }

      /**
       * 复用表单提交流程加入购物车
       */
      addToCart() {
        if (typeof this.form.requestSubmit === "function") {
          this.form.requestSubmit();
        } else {
          this.form.dispatchEvent(
            new Event("submit", { bubbles: true, cancelable: true }),
          );
        }
      }

      /**
       * 打开变体选择面板：将产品页真实的变体选择器移入面板内，
       * 复用主题原生的变体/库存逻辑。
       */
      openVariantPanel() {
        if (!this.variantPanel || !this.variantSlot) return;

        // 每次打开重置已选选项组：用户需在面板内主动选择每个选项
        this.chosenGroups.clear();

        const picker = document.getElementById(
          `Variant-Selects-${this.dataset.section}`,
        );
        // 记录选择器原始位置，关闭时归位
        if (picker && picker.parentElement !== this.variantSlot) {
          this.pickerHome = picker.parentElement;
          this.pickerAnchor = picker.nextElementSibling;
          this.variantSlot.appendChild(picker);
        }

        this.hideMiniCheck();
        this.variantPanel.hidden = false;
        // 下一帧添加 active，保证进入动画触发
        requestAnimationFrame(() =>
          this.variantPanel.classList.add("active"),
        );
        this.variantPanelOpen = true;
        this.miniAddButton?.setAttribute("aria-expanded", "true");

        const firstControl = this.variantPanel.querySelector(
          "select, input:not([type=hidden]):not(.disabled), button:not(.product-mini-variant__close)",
        );
        if (firstControl) firstControl.focus();

        // 点击面板外部关闭
        this.onDocPointer = (e) => {
          if (
            !this.variantPanel.contains(e.target) &&
            e.target !== this.miniAddButton &&
            !this.miniAddButton?.contains(e.target)
          )
            this.closeVariantPanel();
        };
        document.addEventListener("pointerdown", this.onDocPointer);

        // Esc 关闭
        this.onPanelKeydown = (e) => {
          if (e.key === "Escape") this.closeVariantPanel();
        };
        this.variantPanel.addEventListener("keydown", this.onPanelKeydown);
      }

      /**
       * 关闭变体选择面板，并将选择器归位到原始位置
       */
      closeVariantPanel() {
        if (!this.variantPanel) return;

        this.variantPanel.classList.remove("active");
        this.variantPanel.hidden = true;
        this.variantPanelOpen = false;
        // PE 2026-10-04 PDP restyle C30: re-show the bar when the buy block is still out of view
        if (this.buyBlockOutOfView) this.showMiniCheck();
        this.miniAddButton?.setAttribute("aria-expanded", "false");

        const picker = this.variantSlot?.querySelector(
          `#Variant-Selects-${this.dataset.section}`,
        );
        if (picker && this.pickerHome) {
          if (
            this.pickerAnchor &&
            this.pickerAnchor.parentElement === this.pickerHome
          ) {
            this.pickerHome.insertBefore(picker, this.pickerAnchor);
          } else {
            this.pickerHome.appendChild(picker);
          }
        }

        if (this.onDocPointer) {
          document.removeEventListener("pointerdown", this.onDocPointer);
          this.onDocPointer = null;
        }
        if (this.onPanelKeydown) {
          this.variantPanel.removeEventListener("keydown", this.onPanelKeydown);
          this.onPanelKeydown = null;
        }
      }

      /**
       * 选择器值变化：记录已选选项组（按钮/色板/下拉均适用）。
       * 变体解析为异步，实际加入由 variantChange 订阅触发。
       */
      onPickerChange(event) {
        if (!this.variantPanelOpen) return;
        const name = event.target?.name;
        if (name) this.chosenGroups.add(name);
      }

      /**
       * 点击按钮/色板选项：即使点击的是当前已选中的值（不会触发 change），
       * 也视为对该选项组做出了选择。下拉框不在此标记（仅打开不算选择）。
       */
      onPickerClick(event) {
        if (!this.variantPanelOpen || typeof event.target.closest !== "function")
          return;

        const el = event.target.closest(
          ".button-options label, .button-options input, .color-swatches label, .color-swatches input, .color-swatch",
        );
        if (!el) return;

        const container = el.closest(".product-form-input");
        const control = container?.querySelector("input[name]");
        if (!control?.name) return;

        this.chosenGroups.add(control.name);
        // 点击已选中的值不会触发 change/variantChange，下一帧直接尝试加入
        requestAnimationFrame(() => this.maybeAutoAdd());
      }

      /**
       * 所有选项组均已选择且当前变体可购买时，自动加入购物车
       */
      maybeAutoAdd() {
        if (!this.variantPanelOpen || this.isloading) return;
        if (this.chosenGroups.size < this.optionCount) return;

        const available =
          this.mainSubmitButton &&
          !this.mainSubmitButton.hasAttribute("disabled");
        if (!available) return;

        this.addFromVariantPanel();
      }

      /**
       * 面板内选中有效变体：归位选择器后自动加入购物车
       */
      addFromVariantPanel() {
        this.closeVariantPanel();
        this.addToCart();
      }

      /**
       * 获取需要重新渲染的位置
       * @returns {[{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
        ];

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          sections.push({
            id: "Cart-Drawer",
            section: this.cartDrawer.dataset.section,
            selector: "#Cart-Drawer-Details",
          });
        }

        return sections;
      }

      /**
       * 错误显示
       * @param errorMessage
       */
      handleErrorMessage(errorMessage = null) {
        if (this.hideErrors || !this.errorMessageWrapper) return;

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        this.errorMessageWrapper.textContent = errorMessage ? errorMessage : "";
      }

      startLoading() {
        this.isloading = true;

        this.addCartButtons.forEach((button) => {
          button.setAttribute("disabled", "disabled");
          button.classList.add("loading");
        });
      }

      endLoading() {
        this.isloading = false;

        this.addCartButtons.forEach((button) => {
          button.removeAttribute("disabled");
          button.classList.remove("loading");
        });

        // 关闭快速预览
        if (!this.error && this.quickAddDrawer) {
          this.quickAddDrawer.hide();
        }
      }

      showMiniCheck() {
        if (this.miniChecker) this.miniChecker.classList.add("active");
      }

      hideMiniCheck() {
        if (this.miniChecker) this.miniChecker.classList.remove("active");
      }
    },
  );
}
